import React, { useEffect, useState } from 'react'
import styled from '@emotion/styled'
import chunk from 'lodash.chunk'
import { array, string } from 'prop-types'
import { useTheme } from 'emotion-theming'
import { makeImgResizerUrl } from '@jelly/utils'

// Import Components
import { HoverPlusArrowWrapper, HoverPlusArrow, PlusArrowBlockWrapper, PlusWrapper } from '@jelly/components'
import { Link } from '@jelly/ui'

// Styled Components via Emotion
const EvergreenBestPicksWrapper = styled.section`
	width: 100%;
	grid-column: span 2;
	-ms-grid-column-span: 2;
	-ms-grid-column: 1;
	-ms-grid-row: 4;

	${({ theme }) => theme.MQ.M} {
		margin-top: 2rem;
	}
`
const Top3BestPicks = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: space-between;

	${({ theme }) => theme.MQ.M} {
		flex-direction: row;
	}
`
const RestOfBestPicks = styled.div`
	align-items: baseline;
	background: ${({ theme }) => theme.colors.gray[100]};
	display: flex;
	flex-direction: column;
	justify-content: space-around;
	margin: 0 auto 2rem;
	padding: 2rem 0.5rem;

	${({ theme }) => theme.MQ.M} {
		flex-direction: row;
		flex-wrap: wrap;
		margin-bottom: 3rem;
	}
`
const HiddenOverlay = styled.div`
	background: ${({ theme }) => theme.colors.tertiary};
	margin-left: -100%;
	height: 100%;
	position: absolute;
	transition: all ease 1s;
	width: 100%;
`
const CardDescription = styled.div`
	height: auto;
	margin: auto;
	top: 45%;
	margin-left: -45%;
	position: absolute;
	transition: all ease 0.7s;
	visibility: hidden;
	opacity: 0;
	width: 90%;
`
const ThumbnailTitle = styled.div`
	font-size: 28px;
	height: auto;
	left: 0;
	line-height: 2rem;
	margin: auto;
	position: absolute;
	right: 0;
	top: 45%;
	transition: all ease 0.7s;
	width: 98%;
	z-index: 1;

	${({ theme }) => theme.MQ.M} {
		font-size: 24px;
	}
`
const Top3BestPickCard = styled(Link)`
	background: ${({ theme }) =>
			`linear-gradient(${theme.hubTemplate.top3BestPickBackground}, ${theme.hubTemplate.top3BestPickBackground})`},
		url(${({ background }) => background});
	background-repeat: no-repeat;
	background-size: cover;
	color: ${({ theme }) => theme.colors.white};
	display: flex;
	height: 300px;
	margin-bottom: 16px;
	overflow: hidden;
	position: relative;
	text-align: center;
	width: 100%;

	&:hover {
		color: ${({ theme }) => theme.colors.white};

		${PlusWrapper} {
			display: none;
		}
		${HoverPlusArrowWrapper} {
			display: block;
		}
	}

	${({ theme }) => theme.MQ.M} {
		margin-bottom: 0;
		height: 275px;
		width: calc(33% - 5px);
	}

	${({ theme }) => theme.BMQ.ML} {
		${PlusArrowBlockWrapper} {
			height: 50px;
			width: 50px;
		}
	}

	${({ theme }) => theme.MQ.XL} {
		height: 300px;
	}

	@media (hover: hover), screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
		&:hover {
			${HiddenOverlay} {
				margin-left: 0;
			}
			${CardDescription} {
				margin: auto 5% auto 5%;
				top: 45%;
				opacity: 1;
				visibility: visible;
			}
			${({ hasCardDescription }) =>
				hasCardDescription &&
				`${ThumbnailTitle} {
		  		top: 15%;
   		 		${({ theme }) => theme.MQ.M} {
		  			top: 20%;
				 }
			`}
		}
	}
`
const BestPickColumn = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;

	${({ theme }) => theme.MQ.M} {
		width: 50%;
	}

	@media (min-width: 900px) {
		width: 33%;
	}
`
const RestOfBestPickCard = styled.div`
	background: ${({ theme }) => theme.colors.gray[100]};
	display: block;
	margin: 14px 0;
	position: relative;
	text-align: center;

	${({ theme }) => theme.MQ.M} {
		margin: 17px 0;
	}
`
const MoreBestPicksHeader = styled.div`
	background: ${({ theme }) => theme.colors.gray[50]};
	color: ${({ theme }) => theme.colors.tertiary};
	font-size: 24px;
	margin: 2rem auto 0;
	padding: 1.5rem 0;
	text-align: center;
	width: 100%;
`
const Title = styled(Link)`
	color: ${({ theme }) => theme.hubTemplate.bestPickTitleColor};
	font-size: 21px;
	margin: auto;
	width: fit-content;

	&:hover {
		color: ${({ theme }) => theme.hubTemplate.bestPickTitleHoverColor};
	}
`

EvergreenBestPicks.propTypes = {
	categories: array,
	title: string,
}
/**
 * This is the Evergreen Best Picks Section with curated hand-picked Best Picks
 *
 * @param {Array} props.categories List of best picks
 * @param {String} props.title Title of the section
 */
export function EvergreenBestPicks({ categories, title }) {
	const [restOfBestPicksChunked, setRestOfBestPicksChunked] = useState()
	const topCatNum = 3
	const top3BestPicks = categories.slice(0, topCatNum)
	const restOfBestPicks = categories.slice(topCatNum)
	const { MATCH_MEDIA } = useTheme()

	useEffect(() => {
		const handleRestOfBestPicks = () => {
			const isMobile = window.matchMedia(`(max-width: 767px)`).matches
			const isTablet = window.matchMedia(`${MATCH_MEDIA.M} and (max-width: 900px)`).matches
			const numberOfColumns = isMobile ? 1 : isTablet ? 2 : 3
			const chunkNum = Math.ceil(restOfBestPicks.length / numberOfColumns)
			setRestOfBestPicksChunked(chunk(restOfBestPicks, chunkNum))
		}

		handleRestOfBestPicks()
		window.addEventListener('resize', handleRestOfBestPicks)

		return () => window.removeEventListener('resize', handleRestOfBestPicks)
	}, []) // eslint-disable-line

	return (
		<EvergreenBestPicksWrapper>
			<h2 id="productsandservices">{title}</h2>
			<Top3BestPicks>
				{top3BestPicks.map((category, i) => {
					const { canonical, cardDescription, image, subtitle: bestpick } = category
					const bestPickThumbnail = makeImgResizerUrl({ src: image, method: 'h', h: 300 })
					const bestPickOrderNum = i + 1
					return (
						<Top3BestPickCard
							alt={bestpick}
							hasCardDescription={!!cardDescription}
							href={canonical}
							background={bestPickThumbnail}
							key={`${bestpick}-${bestPickOrderNum}`}
						>
							{!!cardDescription && (
								<>
									<HiddenOverlay></HiddenOverlay>
									<CardDescription>{cardDescription}</CardDescription>
								</>
							)}
							<ThumbnailTitle>{bestpick}</ThumbnailTitle>
							<HoverPlusArrow blockHeight={60} blockWidth={60} />
						</Top3BestPickCard>
					)
				})}
			</Top3BestPicks>
			<MoreBestPicksHeader>More Best Picks</MoreBestPicksHeader>
			<RestOfBestPicks>
				{!!restOfBestPicksChunked &&
					restOfBestPicksChunked.map((bestpick, i) => {
						return (
							<BestPickColumn key={`column-${i + 1}`}>
								{bestpick.map((category, i) => {
									const { canonical, subtitle } = category
									return (
										<RestOfBestPickCard key={`${bestpick}-${i}`}>
											<Title alt={subtitle} href={canonical}>
												{subtitle}
											</Title>
										</RestOfBestPickCard>
									)
								})}
							</BestPickColumn>
						)
					})}
			</RestOfBestPicks>
		</EvergreenBestPicksWrapper>
	)
}
